/*****************************************************************
 *  CUSTOMER BUTLER - ONE FIELD'S VALUE: READ, SHOW, SAVE          *
 *  File: apps/web/src/pages/customer/fieldValues.js               *
 *  What it does: for one screen field (the shape the layout door  *
 *  sends: { kind, key, label, dataType, choices, canEdit }):      *
 *   1. reads its value off a customer or property record,         *
 *   2. turns that value into the words the screen shows,          *
 *   3. builds the body a save sends to the record's door.         *
 *  Reads: nothing.  Writes: nothing.  Pure functions.             *
 *****************************************************************/

import { formatDateOnly, formatMoment } from './customerDates';

/*****************************************************************
 *  SECTION 1 - READING A VALUE OFF A RECORD                       *
 *****************************************************************/

/* ---- readFieldValue(record, screenField) -----------------------
   Built-in fields live on the record itself; custom fields live
   in record.customFields under their key.
   In: a record (or null); a screen field.  Out: the raw value.  */
export function readFieldValue(record, screenField) {
  if (!record || !screenField) {
    return undefined;
  }

  if (screenField.kind === 'builtin') {
    return record[screenField.key];
  }

  const customValues = record.customFields || {};

  return customValues[screenField.key];
}

/*****************************************************************
 *  SECTION 2 - THE WORDS A VALUE SHOWS AS                         *
 *****************************************************************/

const ZOHO_EMPTY_CHOICE = '-None-'; // Zoho's word for "nothing picked"

/* ---- isBlankValue(value) ---------------------------------------
   True for nothing at all: no value, empty text, an empty list,
   or Zoho's "-None-".                                           */
export function isBlankValue(value) {
  if (value === null || value === undefined) {
    return true;
  }

  if (typeof value === 'string') {
    const trimmedText = value.trim();

    return trimmedText === '' || trimmedText === ZOHO_EMPTY_CHOICE;
  }

  if (Array.isArray(value)) {
    return value.length === 0;
  }

  return false;
}

/* ---- capitalFirst(word) -- "active" -> "Active" (sentence case) ---- */
function capitalFirst(word) {
  const wordText = String(word);

  if (!wordText) {
    return wordText;
  }

  return wordText.charAt(0).toUpperCase() + wordText.slice(1);
}

/* ---- wordsForObject(value) -------------------------------------
   A Zoho look-up ({ name, id }) shows its name.                 */
function wordsForObject(value) {
  const shownName = value.name || value.display_label || value.displayName || value.id || '';

  return String(shownName);
}

const LEADING_DAY_PATTERN = /^(\d{4}-\d{2}-\d{2})/; // the YYYY-MM-DD at the front of a stored date

/* ---- calendarDayText(value) ------------------------------------
   A DATE field is a calendar day. The server stores a typed day as
   that day's UTC midnight ("2026-10-12T00:00:00.000Z"); read as a
   moment it would slide to Oct 11 in the evening on an Eastern
   clock. So a date field always uses its leading YYYY-MM-DD.    */
export function calendarDayText(value) {
  const dayMatch = LEADING_DAY_PATTERN.exec(String(value));

  if (!dayMatch) {
    return String(value);
  }

  return dayMatch[1];
}

/* ---- showFieldValue(screenField, value) ------------------------
   The words for one value. Blank comes back as '' (the screen
   draws its own quiet dash).
   In: a screen field; the raw value.  Out: text.                */
export function showFieldValue(screenField, value) {
  if (isBlankValue(value)) {
    return '';
  }

  const dataType = screenField ? screenField.dataType : 'text';

  if (dataType === 'boolean') {
    const isYes = value === true || value === 'true';

    return isYes ? 'Yes' : 'No';
  }

  if (dataType === 'date') {
    return formatDateOnly(calendarDayText(value));
  }

  if (dataType === 'datetime') {
    return formatMoment(value);
  }

  if (Array.isArray(value)) {
    const itemWords = value.map((oneItem) => (
      oneItem && typeof oneItem === 'object' ? wordsForObject(oneItem) : String(oneItem)
    ));

    return itemWords.join(', ');
  }

  if (typeof value === 'object') {
    return wordsForObject(value);
  }

  const isButlerChoice = screenField && screenField.kind === 'builtin' && dataType === 'dropdown';

  if (isButlerChoice) {
    return capitalFirst(value);
  }

  return String(value);
}

/*****************************************************************
 *  SECTION 3 - THE BODY A SAVE SENDS                              *
 *****************************************************************/

/* ---- buildSaveBody(screenField, newValue) ----------------------
   In: a screen field; the value the person chose.
   Out: the JSON body for PUT /customer/customers/:id or
   PUT /customer/properties/:id - a built-in field is sent by its
   column name, a custom field inside customFields.              */
export function buildSaveBody(screenField, newValue) {
  if (screenField.kind === 'builtin') {
    return { [screenField.key]: newValue };
  }

  return { customFields: { [screenField.key]: newValue } };
}

/* ---- startingEditValue(screenField, value) ---------------------
   What an editor box starts with: a list for multi-choice and
   tags, true/false for a tick box, text for the rest ('' when
   blank, and "-None-" counts as blank).                         */
export function startingEditValue(screenField, value) {
  const dataType = screenField.dataType;

  if (dataType === 'multiselect' || dataType === 'tags') {
    return Array.isArray(value) ? value.slice() : [];
  }

  if (dataType === 'boolean') {
    return value === true || value === 'true';
  }

  if (isBlankValue(value)) {
    return '';
  }

  if (dataType === 'date') {
    return calendarDayText(value);
  }

  if (typeof value === 'object') {
    return wordsForObject(value);
  }

  return String(value);
}
